import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { message } from "antd";
import type { Dayjs } from "dayjs";

import styles from "./CasinoBetsTable.module.css";
import CasinoBetsFilters from "./CasinoBetsFilters";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";

import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import {
  Button,
  DataTable as DataTableUntyped,
  StatusBadge,
  useExternalTableFilters,
  filterQueryUtils,
} from "../../../shared";
import type { StatusBadgeTone } from "../../../shared";

import { KPI_CARD_BACKGROUND } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatDateNoTimezone,
  getAdminNow,
} from "../../../../utils/dateService";
import { formatLargeNumbers } from "../../../../utils/NumberFormatter";
import {
  deriveCasinoBetStatus,
  getPlacementTransactionId,
  getSettlementTransactionId,
  isCasinoBetSettled,
} from "../../../../utils/CasinoBetTableUtils";

import { useGetUserCasinoBets } from "../../../../queries/user-manager/useGetUserCasinoBets";
import { useGetBetFilterDropdowns } from "../../../../queries/user-manager/useGetBetFilterDropdowns";
import { useSearchUserCasinoBet } from "../../../../queries/user-manager/useSearchUserCasinoBet";
import type {
  CasinoBet,
  CasinoSortBy,
  CasinoSortOrder,
  UserBetsPagination,
} from "../../../../types/bets";
import {
  moneyTypeLabel,
  relabelMoneyTypeOptions,
} from "../../../../utils/MoneyTypeLabels";

const { resolveDateRangeParams, resolveServerSorter } = filterQueryUtils;

// DataTable is a JS module without types; cast to a typed FC for the props this component uses.
type CasinoDataTableProps = {
  columns: Array<Record<string, unknown>>;
  dataSource: CasinoBet[];
  scrollPreset?: string;
  scrollOverrides?: { x?: number; y?: number };
  loading?: boolean;
  onChange?: (p: unknown, f: unknown, s: unknown) => void;
  pagination: false | UserCasinoPagination;
};

type UserCasinoPagination = {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, size: number) => void;
  showSizeChanger?: boolean;
  pageSizeOptions?: readonly string[] | string[];
  showQuickJumper?: boolean;
  hideOnSinglePage?: boolean;
  itemRender?: (
    page: number,
    type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
    originalElement: React.ReactNode,
  ) => React.ReactNode;
};

const TypedDataTable =
  DataTableUntyped as unknown as React.FC<CasinoDataTableProps>;

type UserData = { user_id?: string };

interface CasinoBetsTableProps {
  userData?: UserData | null;
  currencySymbol?: string;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  adminData?: unknown;
}

type DateRange = [Dayjs, Dayjs] | null;

type Summary = {
  total_wager: number;
  total_payout: number;
};

const EMPTY_SUMMARY: Summary = {
  total_wager: 0,
  total_payout: 0,
};

const CASINO_BET_STATUS_LABEL: Record<string, string> = {
  bet_won: "WON",
  bet_lose: "LOST",
  bet_rollback: "ROLLBACK",
  bet_placement: "PLACED",
};

const CASINO_BET_STATUS_TONE: Record<string, StatusBadgeTone> = {
  bet_won: "success",
  bet_lose: "danger",
  bet_rollback: "warning",
  bet_placement: "neutral",
};

const EMPTY_PAGINATION: UserBetsPagination = {
  current_page: 1,
  page_size: 20,
  total_results: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

const DEFAULT_PAGE_SIZE = 20;
// Ant Design's built-in Table.pagination expects `pageSizeOptions` as strings.
const PAGE_SIZE_OPTIONS: readonly string[] = ["10", "20", "50", "100"];

const CasinoBetsTable: React.FC<CasinoBetsTableProps> = ({
  userData,
  currencySymbol,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const todayRange: [Dayjs, Dayjs] = useMemo(
    () => [getAdminNow().startOf("day"), getAdminNow().endOf("day")],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [casinoBetsDates, setCasinoBetsDates] = useState<DateRange>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [cursorsByPage, setCursorsByPage] = useState<
    Record<number, string | Record<string, unknown> | null>
  >({ 1: null });
  const [cachedTotalsJson, setCachedTotalsJson] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary>(EMPTY_SUMMARY);
  const [sortBy, setSortBy] = useState<CasinoSortBy | null>(null);
  const [sortOrder, setSortOrder] = useState<CasinoSortOrder | null>(null);
  const [searchMode, setSearchMode] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<CasinoBet | null>(null);

  const {
    filters,
    normalizedFilters,
    setFilter,
    resetFilters,
    hasActiveFilters,
  } = useExternalTableFilters({
    searchValue: "",
    status: [] as string[],
    money_type: "",
    provider: "",
    game_id: "",
  }) as unknown as {
    filters: {
      searchValue: string;
      status: string[];
      money_type: string;
      provider: string;
      game_id: string;
    };
    normalizedFilters: {
      searchValue: string;
      status: string[];
      money_type: string;
      provider: string;
      game_id: string;
    };
    setFilter: (key: string, value: string | string[]) => void;
    resetFilters: () => void;
    hasActiveFilters: () => boolean;
  };

  const [appliedStatus, setAppliedStatus] = useState<string[]>([]);
  const [appliedMoneyType, setAppliedMoneyType] = useState<string>("");
  // Holds the provider LABEL (e.g. "bgaming"), not the raw option value
  // (which may be "aggregator:bgaming"). The BE filters by `sub_provider`
  // per the PAMENG-1846 contract, and `sub_provider` is the label. If the
  // BE ever exposes a stable provider id, swap to that and remove this note.
  const [appliedProvider, setAppliedProvider] = useState<string>("");
  const [appliedGameId, setAppliedGameId] = useState<string>("");

  // Single dropdowns fetch — the `provider` param only scopes the `games`
  // list per the BE contract; statuses/currencies/providers come back the
  // same regardless, so we don't need a second unscoped call.
  const { data: filterDropdowns, isFetching: isFetchingDropdowns } =
    useGetBetFilterDropdowns("casino", filters.provider || undefined);
  const statusOptions = filterDropdowns?.statuses ?? [];
  const currencyOptions = useMemo(
    () => relabelMoneyTypeOptions(filterDropdowns?.currencies ?? []),
    [filterDropdowns?.currencies],
  );
  const providerOptions = filterDropdowns?.providers ?? [];
  const gameOptions = filters.provider ? (filterDropdowns?.games ?? []) : [];
  const isLoadingGames = Boolean(filters.provider) && isFetchingDropdowns;

  const effectiveDateRange = casinoBetsDates ?? todayRange;
  const allTime = casinoBetsDates === null;
  const { startDate, endDate } = resolveDateRangeParams({
    queryString: effectiveDateRange,
    isQueryString: false,
    allTime,
  });

  const queryParams =
    userData?.user_id && !searchMode
      ? {
          user_id: userData.user_id,
          page,
          page_size: pageSize,
          start_date: startDate ?? undefined,
          end_date: endDate ?? undefined,
          last_evaluated_key: cursorsByPage[page] ?? null,
          ...(page > 1 && cachedTotalsJson ? { totals: cachedTotalsJson } : {}),
          ...(sortBy && sortOrder
            ? { sort_by: sortBy, sort_order: sortOrder }
            : {}),
          ...(appliedStatus.length > 0 ? { status: appliedStatus } : {}),
          ...(appliedMoneyType ? { money_type: appliedMoneyType } : {}),
          ...(appliedProvider ? { provider: appliedProvider } : {}),
          ...(appliedGameId ? { game_id: appliedGameId } : {}),
        }
      : null;

  const query = useGetUserCasinoBets(queryParams);
  const searchMutation = useSearchUserCasinoBet();

  const isKpisLoading = page === 1 && query.isFetching;

  // Sync cursor map when a page response arrives.
  useEffect(() => {
    const nextKey = query.data?.last_evaluated_key;
    if (nextKey === undefined) return;
    setCursorsByPage((prev) => {
      const serialize = (k: unknown) =>
        k === null || k === undefined
          ? ""
          : typeof k === "string"
            ? k
            : JSON.stringify(k);
      const existing = prev[page + 1];
      if (serialize(existing) === serialize(nextKey)) return prev;
      return { ...prev, [page + 1]: nextKey ?? null };
    });
  }, [query.data, page]);

  // Update KPIs only on the first page of a (possibly new) query.
  useEffect(() => {
    const totals = query.data?.totals;
    if (!totals || page !== 1) return;
    setSummary({
      total_wager: totals.total_wagers ?? 0,
      total_payout: totals.total_payouts ?? 0,
    });
    setCachedTotalsJson(JSON.stringify(totals));
  }, [query.data, page]);

  // Surface fetch errors via the shared expired-token handler.
  useEffect(() => {
    if (query.isError && query.error) {
      const response = (query.error as unknown as { response?: unknown })
        ?.response;
      handleExpiredTokenError(navigate, response);
    }
  }, [query.isError, query.error, navigate]);

  const betsToShowInTable: CasinoBet[] = useMemo(() => {
    if (searchMode) {
      return searchResult ? [searchResult] : [];
    }
    return query.data?.items ?? [];
  }, [searchMode, searchResult, query.data]);

  const pagination: UserBetsPagination = useMemo(() => {
    if (searchMode) {
      return {
        current_page: 1,
        page_size: 10,
        total_results: searchResult ? 1 : 0,
        total_pages: searchResult ? 1 : 0,
        has_next: false,
        has_prev: false,
      };
    }
    return query.data?.pagination ?? EMPTY_PAGINATION;
  }, [searchMode, searchResult, query.data]);

  const isApiRunning =
    searchMutation.isPending || (!searchMode && query.isFetching);

  const resetPaginationState = useCallback(() => {
    setCachedTotalsJson(null);
    setPage(1);
    setCursorsByPage({ 1: null });
  }, []);

  const handleDateChange = useCallback(() => {
    // CustomDatePicker already updates casinoBetsDates via setDates; we only
    // reset pagination/KPI/search state here.
    setSortBy(null);
    setSortOrder(null);
    setSearchMode(false);
    setSearchResult(null);
    resetPaginationState();
  }, [resetPaginationState]);

  const handleTableChange = useCallback(
    (_p: unknown, _f: unknown, sorter: unknown) => {
      const { sortBy: nextSortBy, sortOrder: nextSortOrder } =
        resolveServerSorter(sorter);

      // Ant Design onChange fires for pagination too; skip if sort hasn't changed.
      if (nextSortBy === sortBy && nextSortOrder === sortOrder) {
        return;
      }

      setSearchMode(false);
      setSearchResult(null);

      if (nextSortBy && nextSortOrder) {
        setSortBy(nextSortBy as CasinoSortBy);
        setSortOrder(nextSortOrder as CasinoSortOrder);
        resetPaginationState();
        return;
      }
      if (sortBy || sortOrder) {
        setSortBy(null);
        setSortOrder(null);
        resetPaginationState();
      }
    },
    [resetPaginationState, sortBy, sortOrder],
  );

  const handleSearchValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFilter("searchValue", event?.target?.value);
    },
    [setFilter],
  );

  const handleStatusFilterChange = useCallback(
    (next: string[] | undefined) => {
      setFilter("status", next ?? []);
    },
    [setFilter],
  );

  const handleMoneyTypeFilterChange = useCallback(
    (next: string | undefined) => {
      setFilter("money_type", next ?? "");
    },
    [setFilter],
  );

  const handleProviderFilterChange = useCallback(
    (next: string | undefined) => {
      // Provider change wipes the draft game — keeping a game from a
      // different provider would orphan the filter.
      setFilter("provider", next ?? "");
      setFilter("game_id", "");
    },
    [setFilter],
  );

  const handleGameFilterChange = useCallback(
    (next: string | undefined) => {
      setFilter("game_id", next ?? "");
    },
    [setFilter],
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedStatus(normalizedFilters.status ?? []);
    setAppliedMoneyType(normalizedFilters.money_type ?? "");
    setAppliedProvider(normalizedFilters.provider ?? "");
    setAppliedGameId(normalizedFilters.game_id ?? "");
    // Exit search-by-ID mode and clear the input so the user isn't left with
    // a stale bet ID showing while the table re-renders the filtered list.
    setFilter("searchValue", "");
    setSearchMode(false);
    setSearchResult(null);
    resetPaginationState();
  }, [
    normalizedFilters.status,
    normalizedFilters.money_type,
    normalizedFilters.provider,
    normalizedFilters.game_id,
    setFilter,
    resetPaginationState,
  ]);

  const handleClearFilters = useCallback(() => {
    resetFilters();
    setAppliedStatus([]);
    setAppliedMoneyType("");
    setAppliedProvider("");
    setAppliedGameId("");
    setSearchMode(false);
    setSearchResult(null);
    resetPaginationState();
  }, [resetFilters, resetPaginationState]);

  const handleResetSearch = useCallback(() => {
    setFilter("searchValue", "");
    setSearchMode(false);
    setSearchResult(null);
    resetPaginationState();
  }, [setFilter, resetPaginationState]);

  const handleSearchPressed = useCallback(async () => {
    if (!userData?.user_id || !normalizedFilters.searchValue) return;
    try {
      const bet = await searchMutation.mutateAsync({
        user_id: userData.user_id,
        bet_id: String(normalizedFilters.searchValue),
      });
      setSearchResult(bet ?? null);
      setSearchMode(true);
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(
        err?.response?.data?.message || AppStrings.ERROR_SEARCHING_BETS,
      );
      handleExpiredTokenError(navigate, err?.response);
    }
  }, [userData, normalizedFilters, searchMutation, navigate]);

  // Auto-fire single-bet search when arriving with ?betId from the Users page.
  // Strips betId from the URL after firing so refresh doesn't re-trigger.
  const autoSearchFiredRef = useRef(false);
  useEffect(() => {
    autoSearchFiredRef.current = false;
  }, [userData?.user_id]);
  useEffect(() => {
    if (autoSearchFiredRef.current) return;
    if (!userData?.user_id) return;
    const params = new URLSearchParams(location.search);
    const betId = params.get("betId");
    if (!betId) return;
    autoSearchFiredRef.current = true;

    setFilter("searchValue", betId);

    (async () => {
      try {
        const bet = await searchMutation.mutateAsync({
          user_id: userData.user_id as string,
          bet_id: betId,
        });
        setSearchResult(bet ?? null);
        setSearchMode(true);
      } catch (error) {
        const err = error as { response?: { data?: { message?: string } } };
        message.error(
          err?.response?.data?.message || AppStrings.ERROR_SEARCHING_BETS,
        );
        handleExpiredTokenError(navigate, err?.response);
      }
    })();

    params.delete("betId");
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
  }, [
    userData?.user_id,
    location.search,
    location.pathname,
    searchMutation,
    setFilter,
    navigate,
  ]);

  const handlePageSizeChange = useCallback(
    (nextSize: number) => {
      setPageSize(nextSize);
      // Cursor-based pagination assumes a fixed page_size, so a change
      // must invalidate every cached cursor and totals blob and restart
      // from page 1.
      resetPaginationState();
    },
    [resetPaginationState],
  );

  // Ant Design's Table.pagination.onChange also fires when the user
  // switches page size — in that case `nextSize` differs from the current
  // state. Route that through handlePageSizeChange so cursor/cache
  // invariants stay in sync.
  const handlePaginationChange = useCallback(
    (nextPage: number, nextSize: number) => {
      if (nextSize !== pageSize) {
        handlePageSizeChange(nextSize);
        return;
      }
      // Cursor-based pagination only has next cursor; restrict to adjacent pages.
      if (Math.abs(nextPage - pagination.current_page) > 1) return;
      setPage(nextPage);
    },
    [pageSize, handlePageSizeChange, pagination.current_page],
  );

  const handleMoneyType = useCallback((record: CasinoBet) => {
    const placementMoneyType = record?.transactions?.find(
      (transaction) =>
        transaction.title === "bet_placement",
    )?.money_type;

    if (!placementMoneyType || placementMoneyType === record?.money_type) {
      return moneyTypeLabel(record?.money_type);
    }
    return `${moneyTypeLabel(placementMoneyType)} / ${moneyTypeLabel(record?.money_type)}`;
  }, []);

  const columns = useMemo<Array<Record<string, unknown>>>(
    () => [
      {
        title: AppStrings.DATE_TIME,
        dataIndex: "created_at",
        key: "created_at",
        width: 200,
        render: (_text: unknown, record: CasinoBet) =>
          formatDateNoTimezone(record?.created_at, "DD MMMM YYYY, HH:mm:ss"),
      },
      {
        title: AppStrings.ID,
        dataIndex: "bet_id",
        key: "bet_id",
        width: 250,
        render: (_text: unknown, record: CasinoBet) => {
          const placementId = getPlacementTransactionId(record);
          const settlementId = getSettlementTransactionId(record);
          return (
            <div className={styles.casinoBetIdCell}>
              <span className={styles.casinoBetIdPrimary}>
                {placementId ?? "-"}
              </span>
              {settlementId && (
                <span
                  className={styles.casinoBetIdSecondary}
                  data-testid="casino-bet-id-secondary"
                >
                  {settlementId}
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: AppStrings.PROVIDER,
        dataIndex: "provider",
        key: "provider",
        width: 150,
        render: (_text: unknown, record: CasinoBet) =>
          record?.sub ? record?.sub : record?.provider,
      },
      {
        title: AppStrings.MONEY_TYPE,
        dataIndex: "money_type",
        key: "money_type",
        width: 250,
        render: (_text: unknown, record: CasinoBet) => handleMoneyType(record),
      },
      {
        title: AppStrings.GAME,
        dataIndex: "game_name",
        key: "game_name",
        width: 150,
        render: (_text: unknown, record: CasinoBet) => record?.game_name ?? "-",
      },
      {
        title: AppStrings.WAGER,
        dataIndex: "wager",
        key: "wager",
        width: 150,
        sorter: true,
        sortOrder:
          sortBy === "wager"
            ? sortOrder === "asc"
              ? "ascend"
              : "descend"
            : null,
        render: (_text: unknown, record: CasinoBet) => {
          const wagerValue = Number(record?.wager);
          if (!Number.isFinite(wagerValue)) return "-";
          return formatLargeNumbers(wagerValue, 2);
        },
      },
      {
        title: AppStrings.PAYOUT,
        dataIndex: "payout",
        key: "payout",
        width: 150,
        sorter: true,
        sortOrder:
          sortBy === "payout"
            ? sortOrder === "asc"
              ? "ascend"
              : "descend"
            : null,
        render: (_text: unknown, record: CasinoBet) => {
          if (!isCasinoBetSettled(record)) return "-";
          const payoutValue = Number(record?.payout);
          if (!Number.isFinite(payoutValue)) return "-";
          return formatLargeNumbers(payoutValue, 2);
        },
      },
      {
        title: AppStrings.STATUS,
        dataIndex: "title",
        key: "title",
        width: 200,
        render: (_text: unknown, record: CasinoBet) => {
          const statusKey = deriveCasinoBetStatus(record);
          const label = CASINO_BET_STATUS_LABEL[statusKey] ?? "PLACED";
          const tone = CASINO_BET_STATUS_TONE[statusKey] ?? "neutral";
          return <StatusBadge tone={tone}>{label}</StatusBadge>;
        },
      },
      {
        title: AppStrings.BALANCE,
        dataIndex: "balance",
        key: "balance",
        width: 100,
        render: (text: unknown) => {
          const balance = formatLargeNumbers(text as string | number, 2);
          const numeric = Number(text);
          return Number.isFinite(numeric) && numeric < 0 ? "N/A" : balance;
        },
      },
    ],
    [sortBy, sortOrder, handleMoneyType],
  );

  return (
    <div className={styles.casinoBetsContainer}>
      <div className={styles.casinoBetsHeader}>
        <div className="kpi-cards-container">
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.TOTAL_WAGER}
            value={summary.total_wager}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.TOTAL_PAYOUT}
            value={summary.total_payout}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.GGR}
            value={summary.total_wager - summary.total_payout}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        <CustomDatePicker
          getData={handleDateChange}
          dates={casinoBetsDates}
          setDates={setCasinoBetsDates}
          showPresets={true}
          startRange={AppStrings.TODAY}
          allowClearToNull
        />
      </div>

      <div className={styles.casinoBetsTableContainer}>
        <CasinoBetsFilters
          statusOptions={statusOptions}
          currencyOptions={currencyOptions}
          providerOptions={providerOptions}
          gameOptions={gameOptions}
          isLoadingGames={isLoadingGames}
          status={filters.status}
          moneyType={filters.money_type}
          provider={filters.provider}
          gameId={filters.game_id}
          searchValue={filters.searchValue}
          onStatusChange={handleStatusFilterChange}
          onMoneyTypeChange={handleMoneyTypeFilterChange}
          onProviderChange={handleProviderFilterChange}
          onGameChange={handleGameFilterChange}
          onSearchValueChange={handleSearchValueChange}
          onSearchPressed={handleSearchPressed}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          hasAppliedOrDraftFilters={
            hasActiveFilters() ||
            appliedStatus.length > 0 ||
            Boolean(appliedMoneyType) ||
            Boolean(appliedProvider) ||
            Boolean(appliedGameId)
          }
        />

        <div className={styles.casinoBetsTable}>
          <TypedDataTable
            dataSource={betsToShowInTable}
            columns={columns}
            scrollPreset="wide"
            scrollOverrides={{ x: 800 }}
            loading={isApiRunning}
            onChange={handleTableChange}
            pagination={
              !searchMode && pagination.total_results > pageSize
                ? {
                    current: pagination.current_page,
                    // Local state wins over the BE echo: the dropdown
                    // must stay pinned to the admin's selection even if
                    // the BE ignores the `page_size` param (old contract).
                    pageSize,
                    total: pagination.total_results,
                    onChange: (nextPage, nextSize) =>
                      handlePaginationChange(nextPage, nextSize),
                    showSizeChanger: true,
                    pageSizeOptions: [...PAGE_SIZE_OPTIONS],
                    showQuickJumper: false,
                    hideOnSinglePage: true,
                    itemRender: (page, type, originalElement) => {
                      if (type === "page") {
                        const isDisabled =
                          Math.abs(page - pagination.current_page) > 1;
                        return isDisabled ? (
                          <span style={{ opacity: 0.4, pointerEvents: "none" }}>
                            {page}
                          </span>
                        ) : (
                          originalElement
                        );
                      }
                      if (type === "jump-prev" || type === "jump-next")
                        return null;
                      return originalElement;
                    },
                  }
                : false
            }
          />
        </div>

        {searchMode && (
          <div className={styles.resetTableButtonContainer}>
            <Button
              intent="secondary"
              size="large"
              onClick={handleResetSearch}
              data-testid="casino-bets-reset-table"
            >
              {AppStrings.RESET_TABLE}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CasinoBetsTable;
